import { hsl, map, interpolate, gradientAt } from "./helpers/color.js";
import isMobile from "./helpers/mobile.js";

const N = "north";
const S = "south";
const W = "west";
const E = "east";

let SIZE = 60.0;
const DEPTH = 0.66;

function rad(deg) {
    return deg * (Math.PI / 180.0);
}

function pointsAt(angle) {
    const lighter = [Math.cos(rad(90 + angle)), Math.sin(rad(90 + angle))];
    const light = [Math.cos(rad(30 + angle)), Math.sin(rad(30 + angle))];
    const dark = [Math.cos(rad(210 + angle)), Math.sin(rad(210 + angle))];
    const darker = [Math.cos(rad(270 + angle)), Math.sin(rad(270 + angle))];

    return [
        map(lighter[1], -1, 1, 0, 1),
        map(light[1], -1, 1, 0, 1),
        map(dark[1], -1, 1, 0, 1),
        map(darker[1], -1, 1, 0, 1)
    ];
}

function colorsAt(light, dark, angle) {
    function interpolateEach(p, a, b) {
        return a.map((c, i) => interpolate(p, c, b[i]));
    }

    return pointsAt(angle).map((p) => interpolateEach(p, light, dark));
}

function colorFor(direction, progress) {
    const gradient = gradientAt(progress);
    const light = gradient.at(0).at(1);
    const dark = gradient.at(4).at(1);
    const isLandscape = window.innerWidth > window.innerHeight;
    const colors = colorsAt(light, dark, map(progress, 0.0, 1.0, -200, 200));

    if (isLandscape) {
        switch (direction) {
            case N:
                return colors[0];

            case S:
                return colors[3];

            case E:
                return colors[1];

            case W:
                return colors[2];
        }
    } else {
        switch (direction) {
            case N:
                return colors[1];

            case S:
                return colors[2];

            case E:
                return colors[3];

            case W:
                return colors[0];
        }
    }
}

function gradientFor(context, direction, x, y, progress, range = 0.15) {
    const [r, g, b] = colorFor(direction, progress);
    const [h, s, l] = hsl(r, g, b);
    const isLandscape = window.innerWidth > window.innerHeight;

    let x1 = 0,
        y1 = 0,
        x2 = 0,
        y2 = 0;

    if (isLandscape) {
        if (direction === N || direction === S) {
            y1 = y + DEPTH;
            y2 = y - DEPTH;
        } else {
            x1 = x + DEPTH;
            x2 = x - DEPTH;
        }
    } else {
        if (direction === N || direction === S) {
            y1 = y - DEPTH;
            y2 = y + DEPTH;
        } else {
            x1 = x + DEPTH;
            x2 = x - DEPTH;
        }
    }

    const gradient = context.createLinearGradient(
        x1 * SIZE,
        y1 * SIZE,
        x2 * SIZE,
        y2 * SIZE
    );

    let boost = range;

    if (progress < 0.5) {
        boost *= map(progress, 0.0, 0.5, 1.5, 1.0);
    } else if (progress > 0.75) {
        boost *= map(progress, 0.75, 1.0, 1.0, 1.75);
    }

    gradient.addColorStop(
        0,
        `hsl(${h * 360}, ${s * 100}%, ${(l + boost) * 100}%)`
    );

    gradient.addColorStop(
        1,
        `hsl(${h * 360}, ${s * 100}%, ${(l + boost - range) * 100}%)`
    );

    return gradient;
}

const LETTERS = [
    [
        [1, 3],
        [0, 3],
        [0, 0],
        [3, 0],
        [3, 3]
    ],

    [
        [5, 3],
        [5, 0],
        [7, 0],
        [7, 3, true],
        [9, 3, true],
        [9, 0, true],
        [11, 0],
        [11, 3]
    ],

    [
        [16, 3],
        [13, 3],
        [13, 0],
        [16, 0],
        [16, 1]
    ],

    [
        [18, 3],
        [18, 0],
        [20, 0]
    ],

    [
        [22, 3],
        [22, 0]
    ],

    [
        [27, 3],
        [24, 3],
        [24, 0],
        [27, 0]
    ],

    [
        [30, 3],
        [29, 3],
        [29, 0],
        [32, 0],
        [32, 3]
    ]
];

function direction([ax, ay], [bx, by]) {
    if (ax === bx) {
        if (ay > by) {
            return N;
        }

        return S;
    }

    if (ax > bx) {
        return W;
    }

    return E;
}

function createLogotype(canvas, progress) {
    const context = canvas.getContext("2d", { alpha: false });

    function resize() {
        canvas.width = window.innerWidth * window.devicePixelRatio;
        canvas.height =
            document.documentElement.clientHeight * window.devicePixelRatio;
        canvas.style.height = `${document.documentElement.clientHeight}px`;

        const lastLetter = LETTERS.at(-1).at(-1);
        const maximumWidth = lastLetter[0] + DEPTH * 2 + 4;
        const canvasWidth = Math.max(canvas.width, canvas.height);

        SIZE = canvasWidth / maximumWidth;

        render();
    }

    function grid() {
        context.fillStyle = "yellow";

        for (let x = 0; x < canvas.width; x += SIZE) {
            for (let y = 0; y < canvas.height; y += SIZE) {
                context.beginPath();
                context.ellipse(x, y, 1, 1, 0.0, 0.0, Math.PI * 2.0);
                context.fill();
            }
        }
    }

    function path(points) {
        context.beginPath();

        points.forEach(([x, y], index) => {
            if (index === 0) {
                context.moveTo(x * SIZE, y * SIZE);
            } else {
                context.lineTo(x * SIZE, y * SIZE);
            }
        });
    }

    function render() {
        if (!context) {
            return;
        }

        const gradient = context.createLinearGradient(0, 0, 0, canvas.height);

        gradientAt(progress).forEach(([stop, [r, g, b]], index) => {
            const [h, s, l] = hsl(r, g, b);
            const color = `hsl(${h * 360}, ${s * 100}%, ${l * 100}%)`;

            if (index === 0) {
                document
                    .querySelector("meta[name=theme-color]")
                    ?.setAttribute("content", color);
            } else if (index === 4) {
                document.body.style.backgroundColor = color;
            }

            gradient.addColorStop(stop, color);
        });

        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);

        if (isMobile) {
            context.fillStyle = "hsla(0, 100%, 100%, 0.15)";
            context.fillRect(0, 0, canvas.width, 2.0);
        }

        if (canvas.width < canvas.height) {
            context.rotate(Math.PI * 0.5);

            context.translate(
                DEPTH * SIZE + SIZE * 2,
                -SIZE - DEPTH * SIZE - canvas.width * 0.5
            );
        } else {
            context.translate(
                DEPTH * SIZE + SIZE * 2,
                DEPTH * SIZE +
                    (canvas.height - (3 * SIZE + 2 * DEPTH * SIZE)) * 0.5
            );
        }

        LETTERS.forEach((letter) => {
            letter.forEach((point, index) => {
                const [x, y] = point;
                const prev = letter[index - 1];
                const next = letter[index + 1];
                const pd = prev && direction(prev, point);
                const d = next && direction(point, next);

                switch (d) {
                    case W:
                        context.strokeStyle = "yellow";

                        context.fillStyle = gradientFor(
                            context,
                            S,
                            x,
                            y,
                            progress
                        );

                        path([
                            [x, y],
                            [x + DEPTH, y + DEPTH],
                            [next[0] - DEPTH, next[1] + DEPTH],
                            [next[0], next[1]],
                            [x, y]
                        ]);

                        context.fill();

                        if (prev === undefined) {
                            context.fillStyle = gradientFor(
                                context,
                                E,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [x + DEPTH, y - DEPTH],
                                [x + DEPTH, y + DEPTH],
                                [x, y]
                            ]);

                            context.fill();

                            context.fillStyle = gradientFor(
                                context,
                                N,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [x + DEPTH, y - DEPTH],
                                [next[0] + DEPTH, next[1] - DEPTH],
                                [next[0], next[1]],
                                [x, y]
                            ]);

                            context.fill();
                        }

                        break;

                    case N:
                        context.strokeStyle = "blue";

                        context.fillStyle = gradientFor(
                            context,
                            W,
                            x,
                            y,
                            progress
                        );

                        if (next[2]) {
                            path([
                                [x, y],
                                [x - DEPTH, y - DEPTH],
                                [next[0] - DEPTH, next[1] - DEPTH],
                                [next[0], next[1]],
                                [x, y]
                            ]);
                        } else {
                            path([
                                [x, y],
                                [x - DEPTH, y + DEPTH],
                                [next[0] - DEPTH, next[1] - DEPTH],
                                [next[0], next[1]],
                                [x, y]
                            ]);
                        }

                        context.fill();

                        if (prev === undefined) {
                            context.fillStyle = gradientFor(
                                context,
                                S,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [x + DEPTH, y + DEPTH],
                                [x - DEPTH, y + DEPTH],
                                [x, y]
                            ]);

                            context.fill();

                            context.fillStyle = gradientFor(
                                context,
                                E,
                                x,
                                y,
                                progress
                            );

                            if (index === letter.length - 2) {
                                path([
                                    [x, y],
                                    [next[0], next[1]],
                                    [next[0] + DEPTH, next[1] - DEPTH],
                                    [x + DEPTH, y + DEPTH],
                                    [x, y]
                                ]);
                            } else {
                                path([
                                    [x, y],
                                    [next[0], next[1]],
                                    [next[0] + DEPTH, next[1] + DEPTH],
                                    [x + DEPTH, y + DEPTH],
                                    [x, y]
                                ]);
                            }

                            context.fill();
                        } else if (next[2]) {
                            context.fillStyle = gradientFor(
                                context,
                                E,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [next[0], next[1]],
                                [next[0] + DEPTH, next[1] + DEPTH],
                                [x + DEPTH, y + DEPTH],
                                [x, y]
                            ]);

                            context.fill();
                        } else {
                            context.fillStyle = gradientFor(
                                context,
                                E,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [next[0], next[1]],
                                [next[0] + DEPTH, next[1] + DEPTH],
                                [x + DEPTH, y - DEPTH],
                                [x, y]
                            ]);

                            context.fill();
                        }

                        break;

                    case E:
                        context.strokeStyle = "green";

                        if (next[2]) {
                            context.fillStyle = gradientFor(
                                context,
                                S,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [next[0], next[1]],
                                [next[0] + DEPTH, next[1] + DEPTH],
                                [x - DEPTH, y + DEPTH],
                                [x, y]
                            ]);

                            context.fill();

                            context.fillStyle = gradientFor(
                                context,
                                N,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [x + DEPTH, y - DEPTH],
                                [next[0] - DEPTH, next[1] - DEPTH],
                                [next[0], next[1]],
                                [x, y]
                            ]);

                            context.fill();
                        } else {
                            context.fillStyle = gradientFor(
                                context,
                                N,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [x - DEPTH, y - DEPTH],
                                [next[0] + DEPTH, next[1] - DEPTH],
                                [next[0], next[1]],
                                [x, y]
                            ]);

                            context.fill();

                            context.fillStyle = gradientFor(
                                context,
                                S,
                                x,
                                y,
                                progress
                            );

                            if (index === letter.length - 2) {
                                path([
                                    [x, y],
                                    [next[0], next[1]],
                                    [next[0] + DEPTH, next[1] + DEPTH],
                                    [x + DEPTH, y + DEPTH],
                                    [x, y]
                                ]);
                            } else {
                                path([
                                    [x, y],
                                    [next[0], next[1]],
                                    [next[0] - DEPTH, next[1] + DEPTH],
                                    [x + DEPTH, y + DEPTH],
                                    [x, y]
                                ]);
                            }

                            context.fill();
                        }

                        break;

                    case S:
                        context.strokeStyle = "red";

                        if (next[2]) {
                            context.fillStyle = gradientFor(
                                context,
                                E,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [x + DEPTH, y - DEPTH],
                                [next[0] + DEPTH, next[1] - DEPTH],
                                [next[0], next[1]],
                                [x, y]
                            ]);

                            context.fill();
                        } else {
                            context.fillStyle = gradientFor(
                                context,
                                E,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [x + DEPTH, y - DEPTH],
                                [next[0] + DEPTH, next[1] + DEPTH],
                                [next[0], next[1]],
                                [x, y]
                            ]);

                            context.fill();
                        }

                        if (index === letter.length - 2 || next[2]) {
                            context.fillStyle = gradientFor(
                                context,
                                W,
                                x,
                                y,
                                progress
                            );

                            path([
                                [x, y],
                                [next[0], next[1]],
                                [next[0] - DEPTH, next[1] + DEPTH],
                                [x - DEPTH, y + DEPTH],
                                [x, y]
                            ]);

                            context.fill();
                        }

                        break;

                    case undefined:
                        context.strokeStyle = "orange";

                        switch (pd) {
                            case S:
                                context.fillStyle = gradientFor(
                                    context,
                                    S,
                                    x,
                                    y,
                                    progress
                                );

                                path([
                                    [x, y],
                                    [x + DEPTH, y + DEPTH],
                                    [x - DEPTH, y + DEPTH],
                                    [x, y]
                                ]);

                                break;

                            case N:
                                context.fillStyle = gradientFor(
                                    context,
                                    N,
                                    x,
                                    y,
                                    progress
                                );

                                path([
                                    [x, y],
                                    [x - DEPTH, y - DEPTH],
                                    [x + DEPTH, y - DEPTH],
                                    [x, y]
                                ]);

                                break;

                            case E:
                                context.fillStyle = gradientFor(
                                    context,
                                    E,
                                    x,
                                    y,
                                    progress
                                );

                                path([
                                    [x, y],
                                    [x + DEPTH, y - DEPTH],
                                    [x + DEPTH, y + DEPTH],
                                    [x, y]
                                ]);

                                break;
                        }

                        context.fill();
                }
            });
        });
    }

    window.addEventListener("visibilitychange", resize);
    window.addEventListener("resize", resize);
    resize();
}

export default createLogotype;
